import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bug, ChevronLeft, ChevronRight, Lightbulb } from 'lucide-react';
import {
  api, FEEDBACK_STATUS_LABELS,
  type Feedback, type FeedbackKind, type FeedbackStatus,
} from '../lib/api';
import { R, btnAccent, field, ft, label, mono, plural } from '../lib/ui';
import { useDialog } from '../components/DialogHost';

const COLUMNS = '96px minmax(260px,1fr) 150px 96px 128px';
const COLUMNS_NARROW = '92px minmax(140px,1fr) 120px';
const PAGE_SIZE = 9;

type Scope = 'active' | 'archived' | 'all';

/** Узкий экран — список и форма встают друг под друга. */
function useNarrow(breakpoint = 900) {
  const [narrow, setNarrow] = useState(
    typeof window === 'undefined' ? false : window.innerWidth < breakpoint,
  );
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return narrow;
}

function Chip({
  active, onClick, children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 10px',
        fontSize: 12,
        cursor: 'pointer',
        border: `1px solid ${active ? R.accentBorder : R.border}`,
        background: active ? R.accentSubtle : 'transparent',
        color: active ? R.accent : R.textSecondary,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function Count({ children }: { children: React.ReactNode }) {
  return <span style={{ fontFamily: mono, opacity: 0.7 }}>{children}</span>;
}

/** Действие в строке: текст без рамки, подсвечивается при наведении. */
function RowAction({
  children, onClick, danger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  danger?: boolean;
}) {
  const base = danger ? R.danger : R.textMuted;
  return (
    <button
      onClick={onClick}
      style={{ fontSize: 11.5, padding: 0, color: base, background: 'transparent' }}
      onMouseEnter={(e) => (e.currentTarget.style.color = danger ? R.danger : R.accentText)}
      onMouseLeave={(e) => (e.currentTarget.style.color = base)}
    >
      {children}
    </button>
  );
}

function PagerButton({
  onClick, disabled, children,
}: {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        minWidth: 28,
        height: 28,
        padding: '0 8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        color: disabled ? R.textMuted : R.textSecondary,
        background: 'transparent',
        border: `1px solid ${R.border}`,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function Row({
  item, narrow, isAdmin, isMine, onSetStatus, onArchive, onDelete,
}: {
  item: Feedback;
  narrow: boolean;
  isAdmin: boolean;
  isMine: boolean;
  onSetStatus: (status: FeedbackStatus) => void;
  onArchive: (archived: boolean) => void;
  onDelete: () => void;
}) {
  const tone =
    item.status === 'open'
      ? { bg: R.accentSubtle, fg: R.accent, line: R.accentBorder }
      : item.status === 'reviewed'
        ? { bg: 'transparent', fg: R.text, line: R.border }
        : { bg: 'transparent', fg: R.textMuted, line: R.grid };

  const isBug = item.kind === 'bug';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: narrow ? COLUMNS_NARROW : COLUMNS,
        padding: '14px 28px',
        alignItems: 'start',
        borderBottom: ft.hair,
        background: item.status === 'open' ? R.selectionSubtle : 'transparent',
        opacity: item.archived ? 0.7 : 1,
      }}
    >
      <span
        style={{
          justifySelf: 'start',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '2px 7px',
          fontSize: 10.5,
          letterSpacing: '.1em',
          textTransform: 'uppercase',
          border: `1px solid ${isBug ? R.dangerBorder : R.border}`,
          color: isBug ? R.danger : R.text,
        }}
      >
        {isBug ? <Bug size={10} /> : <Lightbulb size={10} />}
        {isBug ? 'Баг' : 'Идея'}
      </span>

      <span
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          paddingRight: 24,
          minWidth: 0,
        }}
      >
        <span style={{ fontSize: 13.5, fontWeight: 600, color: R.text }}>{item.title}</span>

        <span
          title={item.body}
          style={{
            fontSize: 12,
            color: R.textMuted,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.body}
        </span>

        {narrow && (
          <span style={{ fontSize: 11.5, color: R.textMuted }}>
            {item.author?.name ?? '—'} · {new Date(item.createdAt).toLocaleDateString('ru')}
          </span>
        )}

        {(isAdmin || isMine) && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginTop: 4,
              flexWrap: 'wrap',
            }}
          >
            {isAdmin && item.status !== 'reviewed' && (
              <RowAction onClick={() => onSetStatus('reviewed')}>рассмотрено</RowAction>
            )}
            {isAdmin && item.status !== 'rejected' && (
              <RowAction onClick={() => onSetStatus('rejected')}>отказать</RowAction>
            )}
            {isAdmin && item.status !== 'open' && (
              <RowAction onClick={() => onSetStatus('open')}>вернуть в работу</RowAction>
            )}
            {isAdmin && (
              <RowAction onClick={() => onArchive(!item.archived)}>
                {item.archived ? 'из архива' : 'в архив'}
              </RowAction>
            )}
            <RowAction danger onClick={onDelete}>
              удалить
            </RowAction>
          </span>
        )}
      </span>

      {!narrow && (
        <>
          <span
            style={{
              fontSize: 12,
              color: R.textMuted,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              paddingRight: 12,
            }}
          >
            {item.author?.name ?? '—'}
          </span>
          <span
            style={{ fontSize: 12, color: R.textMuted, fontVariantNumeric: 'tabular-nums' }}
          >
            {new Date(item.createdAt).toLocaleDateString('ru')}
          </span>
        </>
      )}

      <span
        style={{
          justifySelf: 'start',
          display: 'inline-flex',
          alignItems: 'center',
          padding: '2px 7px',
          fontSize: 11.5,
          whiteSpace: 'nowrap',
          background: tone.bg,
          color: tone.fg,
          border: `1px solid ${tone.line}`,
        }}
      >
        {item.archived ? 'В архиве' : FEEDBACK_STATUS_LABELS[item.status]}
      </span>
    </div>
  );
}

export function FeedbackPage() {
  const qc = useQueryClient();
  const dialog = useDialog();
  const narrow = useNarrow();

  const [kind, setKind] = useState<FeedbackKind>('suggestion');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [scope, setScope] = useState<Scope>('active');
  const [page, setPage] = useState(0);

  const list = useQuery({ queryKey: ['feedback'], queryFn: api.feedbackList });
  const me = useQuery({ queryKey: ['me'], queryFn: api.me });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['feedback'] });

  const create = useMutation({
    mutationFn: () => api.feedbackCreate(kind, title.trim(), body.trim()),
    onSuccess: () => {
      setTitle('');
      setBody('');
      invalidate();
    },
  });

  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: FeedbackStatus }) =>
      api.feedbackSetStatus(v.id, v.status),
    onSuccess: invalidate,
  });

  const setArchived = useMutation({
    mutationFn: (v: { id: string; archived: boolean }) =>
      api.feedbackSetArchived(v.id, v.archived),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.feedbackDelete(id),
    onSuccess: invalidate,
  });

  const isAdmin = list.data?.isAdmin ?? false;
  const items = list.data?.items ?? [];

  const shown = useMemo(() => {
    if (!isAdmin || scope === 'all') return items;
    return scope === 'archived'
      ? items.filter((i) => i.archived)
      : items.filter((i) => !i.archived);
  }, [items, scope, isAdmin]);

  const counts = useMemo(
    () => ({
      active: items.filter((i) => !i.archived).length,
      archived: items.filter((i) => i.archived).length,
      all: items.length,
    }),
    [items],
  );

  const pages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const pageItems = shown.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  useEffect(() => setPage(0), [scope]);

  const emptyText =
    isAdmin && scope === 'archived'
      ? 'В архиве пусто'
      : isAdmin && scope === 'all'
        ? 'Ещё нет обращений'
        : 'Активных обращений нет';

  const canSend = !!title.trim() && !!body.trim() && !create.isPending;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, background: R.bg }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 16,
          flexWrap: 'wrap',
          padding: '24px 28px 16px',
          borderBottom: ft.strong,
          flex: '0 0 auto',
        }}
      >
        <div>
          <div style={label}>
            {isAdmin ? 'Обратная связь · все обращения' : 'Обратная связь'}
          </div>
          <h2 style={{ margin: '2px 0 0', fontSize: 30, color: R.text }}>Предложения и баги</h2>
        </div>

        {isAdmin && (
          <div style={{ marginLeft: 'auto', alignSelf: 'center', display: 'flex', gap: 8 }}>
            <Chip active={scope === 'active'} onClick={() => setScope('active')}>
              Активные {counts.active > 0 && <Count>{counts.active}</Count>}
            </Chip>
            <Chip active={scope === 'archived'} onClick={() => setScope('archived')}>
              Архив {counts.archived > 0 && <Count>{counts.archived}</Count>}
            </Chip>
            <Chip active={scope === 'all'} onClick={() => setScope('all')}>
              Все <Count>{counts.all}</Count>
            </Chip>
          </div>
        )}
      </header>

      <div
        style={{
          flex: 1,
          display: 'flex',
          minHeight: 0,
          flexDirection: narrow ? 'column' : 'row',
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRight: narrow ? undefined : ft.strong,
            minHeight: 0,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: narrow ? COLUMNS_NARROW : COLUMNS,
              minHeight: 36,
              padding: '0 28px',
              alignItems: 'center',
              background: R.bgSubtle,
              borderBottom: ft.strong,
              ...label,
            }}
          >
            <span>Тип</span>
            <span>Обращение</span>
            {!narrow && (
              <>
                <span>Автор</span>
                <span>Дата</span>
              </>
            )}
            <span>Статус</span>
          </div>

          <div className="rt-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {list.isLoading && (
              <div style={{ padding: 18, fontSize: 12.5, color: R.textMuted }}>Загрузка…</div>
            )}

            {!list.isLoading && pageItems.length === 0 && (
              <div style={{ padding: 24, fontSize: 12.5, color: R.textMuted }}>{emptyText}</div>
            )}

            {pageItems.map((item) => (
              <Row
                key={item.id}
                item={item}
                narrow={narrow}
                isAdmin={isAdmin}
                isMine={item.userId === me.data?.id}
                onSetStatus={async (status) => {
                  if (status === 'rejected') {
                    const ok = await dialog.confirm({
                      title: 'Отклонить обращение?',
                      body: `«${item.title}» будет помечено как отказано.`,
                    });
                    if (!ok) return;
                  }
                  setStatus.mutate({ id: item.id, status });
                }}
                onArchive={(archived) => setArchived.mutate({ id: item.id, archived })}
                onDelete={async () => {
                  const ok = await dialog.confirm({
                    title: 'Удалить обращение?',
                    body: `«${item.title}» будет удалено полностью, без возможности восстановить.`,
                    danger: true,
                  });
                  if (ok) remove.mutate(item.id);
                }}
              />
            ))}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              padding: '8px 28px',
              borderTop: ft.strong,
              background: R.bgSubtle,
              fontSize: 11.5,
              color: R.textMuted,
              flex: '0 0 auto',
            }}
          >
            <span>
              {shown.length} {plural(shown.length, 'обращение', 'обращения', 'обращений')}
            </span>

            {shown.length > PAGE_SIZE && (
              <span
                style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <PagerButton onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                  <ChevronLeft size={13} />
                </PagerButton>
                <span style={{ fontFamily: mono }}>
                  {page + 1} / {pages}
                </span>
                <PagerButton
                  onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
                  disabled={page >= pages - 1}
                >
                  <ChevronRight size={13} />
                </PagerButton>
              </span>
            )}
          </div>
        </div>

        <aside
          className="rt-scroll"
          style={{
            width: narrow ? '100%' : 360,
            flex: narrow ? '0 0 auto' : '0 0 360px',
            background: R.bgSubtle,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflowY: 'auto',
            borderTop: narrow ? ft.strong : undefined,
          }}
        >
          <div style={{ padding: '16px 18px', borderBottom: ft.strong }}>
            <div style={label}>Новое обращение</div>
            <div style={{ fontWeight: 800, fontSize: 17, marginTop: 2, color: R.text }}>
              Что случилось?
            </div>
          </div>

          <div
            style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <div style={{ display: 'flex', border: ft.edge }}>
              {(['bug', 'suggestion'] as FeedbackKind[]).map((k) => (
                <button
                  key={k}
                  onClick={() => setKind(k)}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    padding: '8px 10px',
                    fontSize: 13,
                    cursor: 'pointer',
                    color: kind === k ? R.accent : R.textSecondary,
                    background: kind === k ? R.accentSubtle : 'transparent',
                  }}
                >
                  {k === 'bug' ? <Bug size={13} /> : <Lightbulb size={13} />}
                  {k === 'bug' ? 'Баг' : 'Предложение'}
                </button>
              ))}
            </div>

            <div>
              <div style={{ ...label, marginBottom: 5 }}>Заголовок</div>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Коротко о проблеме"
                style={field}
              />
            </div>

            <div>
              <div style={{ ...label, marginBottom: 5 }}>Описание</div>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Что делали, что ожидали, что получилось"
                rows={5}
                style={{ ...field, minHeight: 110, resize: 'vertical' }}
              />
            </div>

            {create.isError && (
              <div style={{ fontSize: 12, color: R.danger }}>
                {(create.error as Error).message}
              </div>
            )}

            <button
              disabled={!canSend}
              onClick={() => create.mutate()}
              style={{ ...btnAccent, justifyContent: 'flex-start', opacity: canSend ? 1 : 0.5 }}
            >
              {create.isPending ? 'Отправляем…' : 'Отправить'}
            </button>
          </div>

          <div style={{ flex: 1 }} />

          <div
            style={{
              padding: '14px 18px',
              borderTop: ft.strong,
              fontSize: 12,
              color: R.textMuted,
            }}
          >
            Обращение видят администраторы Redwood Tables
          </div>
        </aside>
      </div>
    </div>
  );
}
